(() => {
  const sections = [
    { key: 'details', label: 'Product Details' },
    { key: 'personalization', label: 'Personalization' },
    { key: 'what-is-included', label: 'What’s Included' },
    { key: 'sizing', label: 'Sizing' },
    { key: 'patch-sewing', label: 'Patch Sewing' },
    { key: 'ordering', label: 'Ordering & Turnaround' },
  ];

  const blockTags = new Set([
    'ADDRESS', 'BLOCKQUOTE', 'DIV', 'FIGURE', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'OL', 'P', 'PRE', 'TABLE', 'UL',
  ]);

  const decorativeOnly = /^[\s*•·✦★☆—–_\-=~.]+$/u;
  const updatedVestSizingCopy = 'Sizes Small through 3XL — SAME PRICE! I can now get 4XL in the SAME STYLE! — small upcharge.';
  const headingMarkers = [
    { pattern: /^all the trimmings\b/i, key: 'what-is-included' },
    { pattern: /^(?:boats?\s*&\s*commands?|rates?\s*&\s*nick-?names?|customization|personalization|something new)\b|\bvest closures?\b/i, key: 'personalization' },
    { pattern: /^(?:true\s+)?sizing\b|^size chart\b/i, key: 'sizing' },
    { pattern: /^(?:patches?|patch sewing)\b/i, key: 'patch-sewing' },
    { pattern: /^(?:how ordering works|ordering information|ordering|turnaround)\b|fill out all the options/i, key: 'ordering' },
  ];

  const normalizeText = (value) => value.replace(/\*{4,}/g, ' ').replace(/\s+/g, ' ').trim();

  const stripPresentation = (element) => {
    const clone = element.cloneNode(true);
    clone.querySelectorAll('*').forEach((child) => {
      [...child.attributes].forEach((attribute) => {
        if (
          ['align', 'class', 'height', 'id', 'style', 'width'].includes(attribute.name)
          || attribute.name.startsWith('data-mce-')
        ) {
          child.removeAttribute(attribute.name);
        }
      });
    });
    [...clone.attributes].forEach((attribute) => {
      if (
        ['align', 'class', 'height', 'id', 'style', 'width'].includes(attribute.name)
        || attribute.name.startsWith('data-mce-')
      ) {
        clone.removeAttribute(attribute.name);
      }
    });
    clone.querySelectorAll('font').forEach((font) => font.replaceWith(...font.childNodes));
    clone.querySelectorAll('b, strong').forEach((strong) => strong.replaceWith(...strong.childNodes));
    const walker = document.createTreeWalker(clone, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) walker.currentNode.nodeValue = walker.currentNode.nodeValue.replace(/\*{4,}/g, ' ');
    clone.querySelectorAll('br + br').forEach((br) => br.remove());
    clone.querySelectorAll('li').forEach((item) => {
      if (!normalizeText(item.textContent || '') && !item.querySelector('img, iframe, video, table')) item.remove();
    });
    return clone;
  };

  const extractBlocks = (root) => {
    const result = [];
    const walk = (parent) => {
      [...parent.childNodes].forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = normalizeText(node.textContent || '');
          if (text && !decorativeOnly.test(text)) {
            const paragraph = document.createElement('p');
            paragraph.textContent = text;
            result.push(paragraph);
          }
          return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return;

        const directBlockChildren = [...node.children].some((child) => blockTags.has(child.tagName));
        if (directBlockChildren && !['OL', 'TABLE', 'UL'].includes(node.tagName)) {
          walk(node);
          return;
        }

        const clone = stripPresentation(node);
        const text = normalizeText(clone.textContent || '');
        const hasMedia = clone.matches('figure, table') || clone.querySelector('img, iframe, video, table');
        if ((text && !decorativeOnly.test(text)) || hasMedia) result.push(clone);
      });
    };
    walk(root);
    return result;
  };

  const detectSection = (text) => headingMarkers.find((marker) => marker.pattern.test(text))?.key;

  const isFactOnlyBlock = (text) => text.length <= 180 && (
    /^custom made(?:\s+for my company)?\b/i.test(text)
    || /^made in (?:the )?(?:usa|u\.s\.a\.|united states)\b/i.test(text)
    || /^(?:the )?average time to make (?:this|it|one)?\s*(?:is|:)\s*about \d+ weeks?\b/i.test(text)
  );

  const buildFacts = (sourceText) => {
    const facts = [];
    if (/\bcustom made\b/i.test(sourceText)) facts.push({ title: 'Custom Made' });
    if (/\bmade in (?:the )?(?:usa|u\.s\.a\.|united states)\b/i.test(sourceText)) facts.push({ title: 'Made in the USA' });
    const turnaround = sourceText.match(/average time to make(?: this| it| one)?\s*(?:is|:)\s*about\s*(\d+)\s*weeks?/i);
    if (turnaround) facts.push({ title: 'Typical Turnaround', detail: `About ${turnaround[1]} weeks` });
    return facts;
  };

  const splitRecognizedHeading = (text, detectedSection) => {
    const isRecognizedHeading = detectedSection && /^(?:all the trimmings|boats?\s*&\s*commands?|rates?\s*&\s*nick-?names?|customization|personalization|something new|(?:true\s+)?sizing|size chart|patches?|patch sewing|how ordering works|ordering information|ordering|turnaround)\b/i.test(text);
    if (!isRecognizedHeading) return null;
    const colon = text.indexOf(':');
    const headingText = colon > 0 && colon < 80 ? text.slice(0, colon) : text;
    const remainder = colon > 0 && colon < 80 ? text.slice(colon + 1).trim() : '';
    const fragment = document.createDocumentFragment();
    const heading = document.createElement('h3');
    heading.textContent = headingText;
    fragment.appendChild(heading);
    if (remainder) {
      const paragraph = document.createElement('p');
      paragraph.textContent = remainder;
      fragment.appendChild(paragraph);
    }
    return fragment;
  };

  const normalizeBlock = (entry) => {
    const { element, text, detectedSection } = entry;
    const splitHeading = splitRecognizedHeading(text, detectedSection);
    if (splitHeading) return splitHeading;
    if (/^\*\s*\S/.test(text)) {
      const item = document.createElement('li');
      item.textContent = text.replace(/^\*\s*/, '');
      if (/^Sizes Small - 3XL - SAME PRICE!/i.test(item.textContent)) item.textContent = updatedVestSizingCopy;
      return item;
    }
    if (['OL', 'TABLE', 'UL', 'FIGURE', 'BLOCKQUOTE', 'PRE'].includes(element.tagName)) return element;
    const paragraph = document.createElement('p');
    paragraph.append(...element.childNodes);
    if (/^(?:yes!\s*)?you found (?:it|them)!?$/i.test(text)) paragraph.className = 'subvest-product-information__story-callout';
    if (/^(?:-|—)?\s*(?:the )?vest lady\b/i.test(text) || /^jenny dugan\b/i.test(text)) {
      paragraph.className = 'subvest-product-information__signature';
    }
    return paragraph;
  };

  const appendBlocks = (body, blocks) => {
    let list = null;
    blocks.forEach((block) => {
      if (block.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
        list = null;
        body.appendChild(block);
      } else if (block.tagName === 'LI') {
        if (!list) {
          list = document.createElement('ul');
          body.appendChild(list);
        }
        list.appendChild(block);
      } else {
        list = null;
        body.appendChild(block);
      }
    });
  };

  const createFacts = (facts, sectionId) => {
    const region = document.createElement('section');
    region.className = 'subvest-product-information__craftsmanship';
    const heading = document.createElement('h2');
    heading.className = 'subvest-product-information__craftsmanship-heading';
    heading.id = `SubvestCraftsmanship-${sectionId}`;
    heading.textContent = 'Craftsmanship & Service';
    region.setAttribute('aria-labelledby', heading.id);

    const grid = document.createElement('div');
    grid.className = 'subvest-product-information__facts';
    facts.forEach((factData) => {
      const fact = document.createElement('div');
      fact.className = 'subvest-product-information__fact';
      const ornament = document.createElement('span');
      ornament.className = 'subvest-product-information__fact-ornament';
      ornament.setAttribute('aria-hidden', 'true');
      ornament.textContent = '★';
      const copy = document.createElement('span');
      const title = document.createElement('strong');
      title.className = 'subvest-product-information__fact-title';
      title.textContent = factData.title;
      copy.appendChild(title);
      if (factData.detail) {
        const detail = document.createElement('span');
        detail.className = 'subvest-product-information__fact-detail';
        detail.textContent = factData.detail;
        copy.appendChild(detail);
      }
      fact.append(ornament, copy);
      grid.appendChild(fact);
    });
    region.append(heading, grid);
    return region;
  };

  const createModule = (section, sectionId, isAccordion) => {
    const module = document.createElement(isAccordion ? 'details' : 'section');
    module.className = `subvest-product-information__module subvest-product-information__module--${section.key}`;
    if (isAccordion) module.open = true;
    const header = document.createElement(isAccordion ? 'summary' : 'header');
    header.className = 'subvest-product-information__module-header';
    const ornament = document.createElement('span');
    ornament.className = 'subvest-product-information__module-ornament';
    ornament.setAttribute('aria-hidden', 'true');
    ornament.textContent = '★';
    const heading = document.createElement('h2');
    heading.id = `SubvestInformation-${sectionId}-${section.key}`;
    heading.textContent = section.label;
    module.setAttribute('aria-labelledby', heading.id);
    header.append(ornament, heading);

    const body = document.createElement('div');
    body.className = 'subvest-product-information__body';
    appendBlocks(body, section.blocks.map((entry) => normalizeBlock({
      ...entry,
      element: entry.element.cloneNode(true),
    })));
    module.append(header, body);
    return module;
  };

  const buildFlow = (root, availableSections, facts) => {
    const host = root.querySelector('[data-subvest-information-interface]');
    const isAccordion = root.hasAttribute('data-subvest-information-accordion');
    host.replaceChildren();
    const flow = document.createElement('div');
    flow.className = 'subvest-product-information__flow';
    if (!facts.length && availableSections.length === 1) {
      flow.classList.add('subvest-product-information__flow--compact');
    }
    if (facts.length) flow.appendChild(createFacts(facts, root.dataset.sectionId));
    availableSections.forEach((section) => flow.appendChild(createModule(section, root.dataset.sectionId, isAccordion)));
    host.appendChild(flow);
  };

  const initialize = (root) => {
    if (root.dataset.subvestInformationReady === 'true') return;
    const source = root.querySelector('[data-subvest-information-source]');
    if (!source) return;
    const sourceRoot = source.content.firstElementChild;
    const extracted = extractBlocks(sourceRoot);
    const sourceText = normalizeText(sourceRoot.textContent || '');
    const grouped = Object.fromEntries(sections.map((section) => [section.key, { ...section, blocks: [] }]));
    let currentSection = 'details';

    for (let index = 0; index < extracted.length; index += 1) {
      let element = extracted[index];
      let text = normalizeText(element.textContent || '');
      const nextText = normalizeText(extracted[index + 1]?.textContent || '');
      const followingText = normalizeText(extracted[index + 2]?.textContent || '');
      if (
        /\bvest shown to the left is$/i.test(text)
        && /^custom made(?:\s+for my company)?\b/i.test(nextText)
        && /^and made in (?:the )?(?:usa|u\.s\.a\.|united states)\b/i.test(followingText)
      ) {
        const sentence = document.createElement('p');
        sentence.textContent = `${text} ${nextText.replace(/\s*-\s*$/, '')} ${followingText}`;
        element = sentence;
        text = normalizeText(sentence.textContent || '');
        index += 2;
      }
      const detectedSection = detectSection(text);
      if (detectedSection) currentSection = detectedSection;
      if (isFactOnlyBlock(text)) continue;
      grouped[currentSection].blocks.push({ element, text, detectedSection });
    }

    const available = sections.map((section) => grouped[section.key]).filter((section) => section.blocks.length);
    const facts = buildFacts(sourceText);
    if (!available.length && !facts.length) {
      root.remove();
      return;
    }
    buildFlow(root, available, facts);
    root.dataset.subvestInformationReady = 'true';
  };

  const initializeAll = (scope = document) => scope.querySelectorAll('[data-subvest-product-information]').forEach(initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeAll());
  else initializeAll();
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
